import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  type HTMLAttributes,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
} from "react";

import { cn } from "../lib/cn";

/* -------------------------------------------------------------------------- */
/* Types & Context                                                             */
/* -------------------------------------------------------------------------- */

export type NavbarVariant = "default" | "floating" | "transparent";

interface NavbarContextValue {
  variant: NavbarVariant;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
}

const NavbarContext = createContext<NavbarContextValue | null>(null);

function useNavbarContext() {
  const context = useContext(NavbarContext);
  if (!context) {
    throw new Error("Les sous-composants Navbar doivent être utilisés au sein d'un <Navbar />.");
  }
  return context;
}

/* -------------------------------------------------------------------------- */
/* Navbar (Root)                                                              */
/* -------------------------------------------------------------------------- */

export interface NavbarProps extends HTMLAttributes<HTMLElement> {
  /**
   * Style de la navbar :
   * - `default` : Barre haut de page classique pleine largeur avec bordure subtile.
   * - `floating` : En-tête SOTA flottant en pilule avec verre dépoli (glassmorphism) et ombre.
   * - `transparent` : Arrière-plan transparent pour les pages hero ou immersives.
   */
  variant?: NavbarVariant;
  /** Fixe ou colle la barre au haut de l'écran lors du défilement (par défaut: true). */
  sticky?: boolean;
  /** Afficher la bordure inférieure (pour variant="default"). */
  bordered?: boolean;
  children?: ReactNode;
}

export function Navbar({
  variant = "default",
  sticky = true,
  bordered = true,
  className,
  children,
  ...props
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => setIsOpen((prev) => !prev);

  const contextValue: NavbarContextValue = {
    variant,
    isOpen,
    setIsOpen,
    toggle,
  };

  return (
    <NavbarContext.Provider value={contextValue}>
      <header
        className={cn(
          // Pas de `transition-all` : elle inclut la géométrie, donc une barre
          // collante qui s'anime au défilement recalcule la mise en page de tout
          // ce qui suit. Seules la couleur et le matériau évoluent ici.
          "w-full transition-[background-color,border-color] duration-normal ease-standard",
          sticky && "sticky top-0 z-40",
          variant === "default" && [
            "bg-surface/85 backdrop-blur-md",
            bordered && "border-b border-border",
          ],
          variant === "floating" && "py-3 px-4 sm:px-6 pointer-events-none",
          variant === "transparent" && "bg-transparent",
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            "mx-auto flex items-center justify-between transform-gpu transition-[transform,opacity] duration-normal ease-standard",
            variant === "floating"
              ? [
                  "pointer-events-auto max-w-6xl rounded-2xl border border-border/80",
                  "bg-surface/75 backdrop-blur-xl shadow-lg shadow-black/5 dark:shadow-black/20",
                  "px-4 sm:px-6 h-14 sm:h-16",
                ]
              : "max-w-7xl px-4 sm:px-6 h-16",
          )}
        >
          {children}
        </div>
      </header>
    </NavbarContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/* NavbarBrand                                                                */
/* -------------------------------------------------------------------------- */

export interface NavbarBrandProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export function NavbarBrand({ className, children, ...props }: NavbarBrandProps) {
  return (
    <div className={cn("flex items-center gap-3 shrink-0 font-semibold text-fg", className)} {...props}>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* NavbarContent                                                              */
/* -------------------------------------------------------------------------- */

export interface NavbarContentProps extends HTMLAttributes<HTMLDivElement> {
  align?: "left" | "center" | "right";
  children?: ReactNode;
}

export function NavbarContent({
  align = "center",
  className,
  children,
  ...props
}: NavbarContentProps) {
  return (
    <nav
      className={cn(
        "hidden md:flex items-center gap-1 lg:gap-2 text-sm font-medium",
        align === "left" && "mr-auto ml-6",
        align === "center" && "mx-auto",
        align === "right" && "ml-auto mr-6",
        className,
      )}
      {...props}
    >
      {children}
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/* NavbarItem & NavbarLink                                                    */
/* -------------------------------------------------------------------------- */

export interface NavbarItemProps extends HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  children?: ReactNode;
}

export function NavbarItem({ active, className, children, ...props }: NavbarItemProps) {
  return (
    <div className={cn("relative flex items-center", className)} {...props}>
      {children}
    </div>
  );
}

export interface NavbarLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  children?: ReactNode;
}

export function NavbarLink({ active, className, children, ...props }: NavbarLinkProps) {
  return (
    <a
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
        "transition-[color,background-color] duration-fast ease-standard",
        "focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2",
        active
          ? "text-accent-fg bg-accent-subtle/70 font-semibold"
          : "text-fg-muted hover:text-fg hover:bg-surface-sunken/60",
        className,
      )}
      {...props}
    >
      {children}
      {active && (
        <span className="absolute bottom-0 left-3 right-3 h-0.5 origin-center transform-gpu animate-pop rounded-full bg-accent" />
      )}
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/* NavbarActions                                                              */
/* -------------------------------------------------------------------------- */

export interface NavbarActionsProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export function NavbarActions({ className, children, ...props }: NavbarActionsProps) {
  return (
    <div className={cn("flex items-center gap-2.5 shrink-0", className)} {...props}>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* NavbarToggle (Bouton hamburger réactif mobile)                             */
/* -------------------------------------------------------------------------- */

export interface NavbarToggleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  "aria-label"?: string;
}

export function NavbarToggle({
  className,
  "aria-label": ariaLabel = "Basculez le menu de navigation",
  ...props
}: NavbarToggleProps) {
  const { isOpen, toggle } = useNavbarContext();

  return (
    <button
      type="button"
      onClick={toggle}
      aria-expanded={isOpen}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex md:hidden items-center justify-center rounded-lg p-2 text-fg-muted hover:text-fg hover:bg-surface-sunken/80 transition-colors focus-visible:outline-2 focus-visible:outline-ring",
        className,
      )}
      {...props}
    >
      {/* Les trois barres ne bougent qu'en `transform` et en `opacity` : la
          croix se forme sans qu'aucune ligne ne change de boîte. */}
      <div className="relative size-5 flex flex-col justify-between py-1">
        <span
          className={cn(
            "h-0.5 w-5 bg-current rounded-full origin-center transform-gpu will-change-transform",
            "transition-transform duration-slow ease-standard",
            isOpen && "translate-y-[5px] rotate-45",
          )}
        />
        <span
          className={cn(
            "h-0.5 w-5 bg-current rounded-full transition-opacity duration-slow ease-standard",
            isOpen && "opacity-0",
          )}
        />
        <span
          className={cn(
            "h-0.5 w-5 bg-current rounded-full origin-center transform-gpu will-change-transform",
            "transition-transform duration-slow ease-standard",
            isOpen && "-translate-y-[5px] -rotate-45",
          )}
        />
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* NavbarMobileMenu (Tiroir déroulant sur mobile)                             */
/* -------------------------------------------------------------------------- */

export interface NavbarMobileMenuProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export function NavbarMobileMenu({ className, children, ...props }: NavbarMobileMenuProps) {
  const { isOpen } = useNavbarContext();

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "md:hidden w-full border-t border-border/60 bg-surface/95 backdrop-blur-xl px-4 py-4 space-y-2",
        "transform-gpu animate-slide-down",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
